/**
 * toQuoteView — pure `ChargesBreakdownResponseDto` → quote-view mapper
 * (SCRUM-337).
 *
 * The live-quote panel on the create-reservation form never consumes the
 * raw preview breakdown: it consumes a `QuoteView` computed here. Keeping
 * the projection PURE (no hooks, no I/O, no wall-clock read) makes it
 * unit-testable in isolation and keeps the feature a thin renderer — the
 * exact posture of `to-ledger-view.ts` (SCRUM-336),
 * `to-reservation-detail-view.ts` (SCRUM-335), and `to-reservation-row-view.ts`
 * (SCRUM-334).
 *
 * MONEY (CLAUDE.md rule 6 / FE rule §6): every figure is a passed-through
 * server `Cents` field. The mapper NEVER sums, subtracts, or divides —
 * `grandTotalCents` is `breakdown.grandTotalCents` VERBATIM (0 in → 0 out;
 * null in → null out; never recomputed from subtotals). Line-item
 * `valueCents` is `amountCents` verbatim. A `null` subtotal stays `null`
 * (never coerced to `0`) so the feature renders the neutral "—" marker via
 * `formatCents`, never `formatCents(0)` — a real `$0.00` subtotal and an
 * absent/unknown subtotal are distinct.
 */

import type { components } from '@artemis/contracts';
import type { ChargesBreakdownResponseDto } from './types.js';

/**
 * Line-item sub-DTOs — the wire types are the single source of truth
 * (FE rule §4; never hand-typed). Derived from `components['schemas']`.
 */
type RentLineItemResponseDto = components['schemas']['RentLineItemResponseDto'];
type FeeLineItemResponseDto = components['schemas']['FeeLineItemResponseDto'];
type TaxLineItemResponseDto = components['schemas']['TaxLineItemResponseDto'];
type QuoteWarningResponseDto =
  components['schemas']['QuoteWarningResponseDto'];

/** One quote line — label + a passed-through Cents figure (null = absent). */
export interface QuoteLineEntry {
  /** Display label (guest-facing name for fees/taxes; stay date for rent). */
  readonly label: string;
  /** Cents — passthrough. `null` when the server omitted the amount. */
  readonly valueCents: number | null;
}

/** One surfaced quote warning — the wire `code` verbatim (color-independent). */
export interface QuoteWarningEntry {
  readonly code: string;
}

/** The view the live-quote panel renders. */
export interface QuoteView {
  /** `breakdown.nightlyRateSubtotalCents` (passthrough); `null` = absent. */
  readonly nightlyRateSubtotalCents: number | null;
  /** `breakdown.feeSubtotalCents` (passthrough); `null` = absent. */
  readonly feeSubtotalCents: number | null;
  /** `breakdown.taxSubtotalCents` (passthrough); `null` = absent. */
  readonly taxSubtotalCents: number | null;
  /** `breakdown.grandTotalCents` (passthrough — NEVER summed); `null` = absent. */
  readonly grandTotalCents: number | null;
  /** Rent rows (stayDate label + amountCents passthrough). */
  readonly rentLineItems: QuoteLineEntry[];
  /** Fee rows (guestFacingName label + amountCents passthrough). */
  readonly feeLineItems: QuoteLineEntry[];
  /** Tax rows (guestFacingName label + amountCents passthrough). */
  readonly taxLineItems: QuoteLineEntry[];
  /** Surfaced quote warnings (`code` verbatim). */
  readonly warnings: QuoteWarningEntry[];
}

/**
 * Passthrough a Cents subtotal VERBATIM. The wire type is `number`, but a
 * server (or a future nullable widening) may send `null` for an absent
 * subtotal — that `null` must survive UNCHANGED (never coerced to `0`),
 * exactly as `0` survives as `0`. So this returns the value as-is when it
 * is a number, `null` otherwise; it NEVER applies `?? 0` or `?? null` to a
 * real numeric value (that would erase the `0` vs `null` distinction).
 */
function passthroughCents(value: number | null | undefined): number | null {
  return typeof value === 'number' ? value : null;
}

export function toQuoteView(breakdown: ChargesBreakdownResponseDto): QuoteView {
  const b = breakdown as ChargesBreakdownResponseDto & {
    // The generated wire type is non-nullable `number`; a defensive read
    // keeps a null (absent-subtotal) survivable without an arithmetic coerce.
    readonly nightlyRateSubtotalCents: number | null;
    readonly feeSubtotalCents: number | null;
    readonly taxSubtotalCents: number | null;
    readonly grandTotalCents: number | null;
  };

  const rentLineItems = b.rentLineItems.map(
    (rent: RentLineItemResponseDto): QuoteLineEntry => ({
      label: rent.stayDate,
      valueCents: passthroughCents(rent.amountCents),
    }),
  );

  const feeLineItems = b.feeLineItems.map(
    (fee: FeeLineItemResponseDto): QuoteLineEntry => ({
      label: fee.guestFacingName,
      valueCents: passthroughCents(fee.amountCents),
    }),
  );

  const taxLineItems = b.taxLineItems.map(
    (tax: TaxLineItemResponseDto): QuoteLineEntry => ({
      label: tax.guestFacingName,
      valueCents: passthroughCents(tax.amountCents),
    }),
  );

  const warnings = b.warnings.map(
    (warning: QuoteWarningResponseDto): QuoteWarningEntry => ({
      code: warning.code,
    }),
  );

  return {
    nightlyRateSubtotalCents: passthroughCents(b.nightlyRateSubtotalCents),
    feeSubtotalCents: passthroughCents(b.feeSubtotalCents),
    taxSubtotalCents: passthroughCents(b.taxSubtotalCents),
    grandTotalCents: passthroughCents(b.grandTotalCents),
    rentLineItems,
    feeLineItems,
    taxLineItems,
    warnings,
  };
}
